
(() => {
  const normalize = (value) => String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  const start = (root) => {
    if (!root || root.dataset.ingredientReady === "true") return;
    const table = root.querySelector(".en-ik-data table");
    const search = root.querySelector("[data-ik-search]");
    const typeSelect = root.querySelector("[data-ik-type]");
    const countOutput = root.querySelector("[data-ik-count]");
    const suggestions = root.querySelector("[data-ik-suggestions]");
    const suggestionList = root.querySelector("[data-ik-suggestion-list]");
    const alphabet = root.querySelector("[data-ik-alphabet]");
    const clearButton = root.querySelector("[data-ik-clear]");
    const findButton = root.querySelector("[data-ik-go]");
    const noResults = root.querySelector("[data-ik-none]");
    const totalNumber = root.querySelector("[data-ik-total]");
    const typesNumber = root.querySelector("[data-ik-types]");
    if (!table || !table.tBodies.length || !search || !typeSelect || !countOutput) return;

    root.dataset.ingredientReady = "true";
    table.classList.add("en-ik-index");
    table.querySelectorAll("thead th").forEach((cell) => cell.setAttribute("scope", "col"));
    const rows = [...table.tBodies[0].rows];
    const usedIds = new Set();
    const records = rows.map((row, index) => {
      const cells = [...row.cells];
      if (cells.length < 3) return null;
      const name = (cells[0].textContent || "").replace(/\s+/g, " ").trim();
      const inci = (cells[1].textContent || "").replace(/\s+/g, " ").trim();
      const type = (cells[2].textContent || "").replace(/\s+/g, " ").trim();
      const first = normalize(name).charAt(0).toUpperCase();
      const letter = /^[A-Z]$/.test(first) ? first : "#";
      let id = "en-ik-" + (normalize(name).replace(/\s+/g, "-").slice(0, 75) || "ingredient");
      if (usedIds.has(id)) id += "-" + (index + 1);
      usedIds.add(id);
      row.id = id;
      row.dataset.ikLetter = letter;
      row.setAttribute("tabindex", "-1");
      cells[0].querySelectorAll('a[target="_blank"]').forEach((link) => {
        link.rel = "noopener noreferrer";
      });
      return { row, id, name, inci, type, letter, key: normalize(name + " " + inci + " " + type), nameKey: normalize(name) };
    }).filter(Boolean);

    const typeCounts = new Map();
    const firstByLetter = new Map();
    records.forEach((rec) => {
      typeCounts.set(rec.type, (typeCounts.get(rec.type) || 0) + 1);
      if (!firstByLetter.has(rec.letter)) firstByLetter.set(rec.letter, rec);
    });
    if (totalNumber) totalNumber.textContent = String(records.length);
    if (typesNumber) typesNumber.textContent = String(typeCounts.size);

    [...typeCounts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).forEach(([type, count]) => {
      const option = document.createElement("option");
      option.value = type;
      option.textContent = type + " (" + count + ")";
      typeSelect.appendChild(option);
    });

    let visible = records;
    let activeHighlight = null;
    const listOfLetters = ["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];
    if (alphabet) {
      listOfLetters.forEach((letter) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = letter;
        button.disabled = !firstByLetter.has(letter);
        button.setAttribute("aria-label", letter === "#" ? "Jump to ingredients beginning with a number" : "Jump to ingredients starting with " + letter);
        button.addEventListener("click", () => {
          search.value = "";
          typeSelect.value = "";
          update(false);
          const firstRecord = firstByLetter.get(letter);
          if (firstRecord) jump(firstRecord, false);
        });
        alphabet.appendChild(button);
      });
    }

    const keepFocus = (row) => {
      row.focus({ preventScroll: true });
    };
    const jump = (record, narrow = true) => {
      if (!record) return;
      if (narrow) {
        search.value = record.name;
        typeSelect.value = "";
        update(false);
      }
      if (activeHighlight) activeHighlight.classList.remove("en-ik-hit");
      record.row.classList.add("en-ik-hit");
      activeHighlight = record.row;
      suggestions.hidden = true;
      const reducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      record.row.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
      window.setTimeout(() => keepFocus(record.row), reducedMotion ? 0 : 460);
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, "", "#" + encodeURIComponent(record.id));
      }
    };

    const renderSuggestions = (matches) => {
      suggestionList.replaceChildren();
      const typed = search.value.trim();
      if (!typed || matches.length === 0) {
        suggestions.hidden = true;
        return;
      }
      matches.slice(0, 6).forEach((rec) => {
        const item = document.createElement("li");
        item.className = "en-ik-suggestion-item";
        const button = document.createElement("button");
        button.type = "button";
        button.setAttribute("aria-label", "Jump to " + rec.name + " in the ingredients list");
        const inner = document.createElement("span");
        const title = document.createElement("span");
        title.className = "en-ik-suggestion-name";
        title.textContent = rec.name;
        const sub = document.createElement("span");
        sub.className = "en-ik-suggestion-sub";
        sub.textContent = rec.inci + " · " + rec.type;
        const arrow = document.createElement("span");
        arrow.className = "en-ik-suggestion-arrow";
        arrow.setAttribute("aria-hidden", "true");
        arrow.textContent = "↓";
        inner.append(title, sub);
        button.append(inner, arrow);
        button.addEventListener("click", () => jump(rec));
        item.appendChild(button);
        suggestionList.appendChild(item);
      });
      suggestions.hidden = false;
    };

    function update(showSuggestions = true) {
      const tokens = normalize(search.value).split(" ").filter(Boolean);
      const selected = typeSelect.value;
      visible = [];
      records.forEach((rec) => {
        const matches = (!selected || rec.type === selected) && tokens.every((term) => rec.key.includes(term));
        rec.row.hidden = !matches;
        if (matches) visible.push(rec);
      });
      countOutput.textContent = visible.length === records.length
        ? "Showing all " + records.length + " ingredients"
        : "Showing " + visible.length + " of " + records.length + " ingredients";
      clearButton.hidden = !search.value && !selected;
      noResults.hidden = visible.length > 0;
      if (showSuggestions) {
        const ranked = visible.slice().sort((a, b) => {
          const query = normalize(search.value);
          const aRank = a.nameKey === query ? 0 : a.nameKey.startsWith(query) ? 1 : a.nameKey.includes(query) ? 2 : 3;
          const bRank = b.nameKey === query ? 0 : b.nameKey.startsWith(query) ? 1 : b.nameKey.includes(query) ? 2 : 3;
          return aRank - bRank || a.name.localeCompare(b.name);
        });
        renderSuggestions(ranked);
      } else {
        suggestions.hidden = true;
      }
    }

    search.addEventListener("input", () => update());
    typeSelect.addEventListener("change", () => update());
    clearButton.addEventListener("click", () => {
      search.value = "";
      typeSelect.value = "";
      update(false);
      search.focus();
    });
    if (findButton) findButton.addEventListener("click", () => {
      if (visible.length) jump(visible[0], !!search.value.trim());
      else search.focus();
    });
    search.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (visible.length) jump(visible[0], !!search.value.trim());
      } else if (event.key === "ArrowDown" && !suggestions.hidden) {
        const first = suggestionList.querySelector("button");
        if (first) { event.preventDefault(); first.focus(); }
      } else if (event.key === "Escape") {
        suggestions.hidden = true;
      }
    });
    suggestionList.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        suggestions.hidden = true;
        search.focus();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const buttons = [...suggestionList.querySelectorAll("button")];
      const index = buttons.indexOf(document.activeElement);
      if (index === -1) return;
      event.preventDefault();
      const next = index + (event.key === "ArrowDown" ? 1 : -1);
      if (buttons[next]) buttons[next].focus();
      else search.focus();
    });

    update(false);
    const initialHash = decodeURIComponent(window.location.hash.slice(1));
    const initialTarget = records.find((rec) => rec.id === initialHash);
    if (initialTarget) window.setTimeout(() => jump(initialTarget, false), 100);
  };

  const initAll = (base = document) => {
    base.querySelectorAll(".en-ik[data-ingredient-page]").forEach(start);
    if (base.matches && base.matches(".en-ik[data-ingredient-page]")) start(base);
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
